import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { createInfinityTheme, cobalt } from '../design-system';
import { readPilotPreference, savePilotPreference } from '../pilot/pilotPreference';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Login and the Infinity shell use the dark reference treatment by default;
// the toggle remains persisted so operators can choose the light workspace.
const STORAGE_KEY = '24k-infinity-theme-mode-v2';

const readInitialMode = () => {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readInitialMode);
  const pilotEnabled = readPilotPreference();

  useEffect(() => { savePilotPreference(); }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, mode);
    // keep the browser chrome / body in sync with the active mode
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', mode === 'dark' ? '#0A0A0B' : '#F5F5F5');
  }, [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const theme = useMemo(() => createInfinityTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({ mode, toggleMode, accent: cobalt[600], pilotEnabled }),
    [mode, toggleMode, pilotEnabled]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
